import { isApiError } from '@chat-emp/shared';
import { ApiRequestError, clearSession, ensureReauth, getSessionToken } from './session.js';

export { ApiRequestError };

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '';

/** One fetch with the current in-memory session token attached as a Bearer header. */
function send(path: string, init: RequestInit): Promise<Response> {
  const token = getSessionToken();
  const headers = new Headers(init.headers);
  if (token) headers.set('authorization', `Bearer ${token}`);
  return fetch(`${API_BASE}${path}`, { ...init, headers });
}

/** One request, retried once through a fresh session if the first 401s. */
async function sendWithReauth(path: string, init: RequestInit): Promise<Response> {
  const res = await send(path, init);
  if (res.status !== 401) return res;
  // Drop the token we now know is dead before waiting. Minting a fresh handoff
  // is a network round-trip on the host, and everything else the user's click
  // fired lands inside that window — without this they each spend their one
  // retry re-sending the same expired token.
  clearSession();
  await ensureReauth();
  return send(path, init);
}

/**
 * Same-origin fetch wrapper. On a 401 it asks the host to re-establish the
 * session (silent re-handoff) and retries the original request exactly once with
 * the refreshed token. A second 401 surfaces the error.
 */
export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await sendWithReauth(path, init);

  const body: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    if (isApiError(body)) {
      throw new ApiRequestError(body.code, body.message, body.retryable, res.status);
    }
    throw new ApiRequestError('unknown', 'Request failed.', res.status >= 500, res.status);
  }
  return body as T;
}

/** `inline; filename="Aisyah CV 2026.pdf"` → `Aisyah CV 2026.pdf`. */
function fileNameFrom(disposition: string | null): string | undefined {
  if (!disposition) return undefined;
  const extended = /filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i.exec(disposition)?.[1];
  if (extended) {
    try {
      return decodeURIComponent(extended.trim().replace(/^"|"$/g, ''));
    } catch {
      // Malformed escape: fall through to the plain form.
    }
  }
  return /filename\s*=\s*"?([^";]+)"?/i.exec(disposition)?.[1]?.trim() || undefined;
}

export interface BlobResponse {
  blob: Blob;
  /** From Content-Disposition, so the download saves under a real name. */
  fileName?: string;
}

/**
 * A file, through the same session and the same one-shot re-handoff as
 * `apiFetch`. That retry matters more here than elsewhere: the résumé route
 * answers 401 when the session outlived the upstream credential it was issued,
 * and a silent re-handoff mints a new one — so the common case recovers without
 * the employer seeing anything.
 *
 * The error body is JSON even though the success body is not, so failures read
 * exactly like every other route's.
 */
export async function apiFetchBlob(path: string, init: RequestInit = {}): Promise<BlobResponse> {
  const res = await sendWithReauth(path, init);

  if (!res.ok) {
    const body: unknown = await res.json().catch(() => null);
    if (isApiError(body)) {
      throw new ApiRequestError(body.code, body.message, body.retryable, res.status);
    }
    throw new ApiRequestError('unknown', 'Request failed.', res.status >= 500, res.status);
  }

  const fileName = fileNameFrom(res.headers.get('content-disposition'));
  return { blob: await res.blob(), ...(fileName ? { fileName } : {}) };
}
